import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Modal, Select } from 'antd';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import User, { CardDataUser } from '../../user/types/User';
import Course from '../../course/types/Course';
import { useUser } from '../../user/userContext';
import {
  CourseInstanceCardData,
  CourseInstanceStatus,
} from '../types/CourseInstance';
import CourseAPI from '../../course/CourseAPI';
import UserOption from '../../components/UserOption';
import CourseOption from '../../components/CourseOption';
import SearchLoadingIndicator from '../../components/SearchLoadingIndicator';
import useInstructorOptions from '../../user/hooks/useInstructorOptions';

import SwapInstructorModal from './SwapInstructorModal';

export enum FormType {
  SelectInstructorForm = 'instructorId',
  SwapInstructorForm = 'swapInstructor',
  SelectCourseForm = 'courseId',
  ConfirmForm = 'confirm',
  CancelForm = 'cancel',
  DeleteForm = 'delete',
}

interface InstructorForm {
  formType: FormType.SelectInstructorForm;
  instructor: CardDataUser;
}

export type FormValues = InstructorForm;

export interface EditableFormValues {
  instructor?: CardDataUser | null;
  course?: Course;
}

const getTitle = (
  t: TFunction,
  formType?: FormType,
  courseInstanceStatus?: CourseInstanceStatus,
) => {
  switch (formType) {
    case FormType.SelectInstructorForm:
      return t('components.QuickActionEditModal.selectInstructor');
    case FormType.SelectCourseForm:
      return t('components.QuickActionEditModal.selectCourse');
    case FormType.CancelForm:
      return courseInstanceStatus !== CourseInstanceStatus.Canceled
        ? t('components.QuickActionEditModal.cancelCourseInstance')
        : t('components.QuickActionEditModal.undoCancelCourseInstance');
    case FormType.ConfirmForm:
      return courseInstanceStatus !== CourseInstanceStatus.Confirmed
        ? t('components.QuickActionEditModal.confirmCourseInstance')
        : t('components.QuickActionEditModal.undoConfirmCourseInstance');
  }
};

const getSubtitle = (
  t: TFunction,
  formType?: FormType,
  courseInstanceStatus?: CourseInstanceStatus,
) => {
  switch (formType) {
    case FormType.SelectInstructorForm:
      return t('components.QuickActionEditModal.selectInstructorSubtitle');
    case FormType.SelectCourseForm:
      return t('components.QuickActionEditModal.selectCourseSubtitle');
    case FormType.CancelForm:
      return courseInstanceStatus !== CourseInstanceStatus.Canceled
        ? t('components.QuickActionEditModal.cancelCourseInstanceSubtitle')
        : t('components.QuickActionEditModal.undoCancelCourseInstanceSubtitle');
    case FormType.ConfirmForm:
      return courseInstanceStatus !== CourseInstanceStatus.Confirmed
        ? t('components.QuickActionEditModal.confirmCourseInstanceSubtitle')
        : t(
            'components.QuickActionEditModal.undoConfirmCourseInstanceSubtitle',
          );
  }
};

type QuickActionEditModalProps = {
  courseInstance: CourseInstanceCardData;
  formType?: FormType;
  visible: boolean;
  setShowModal?: (value: boolean) => void;
  onConfirm?: (values: EditableFormValues, formType: FormType) => void;
  toggleCourseInstanceConfirmed?: () => void;
  toggleCourseInstanceCanceled?: () => void;
  deleteCourseInstance?: () => void;
  updatingInstructor?: boolean;
  updatingCourse?: boolean;
  updatingStatus?: boolean;
  deletingCourseInstance?: boolean;
  onFilterChange?: () => void;
};

const QuickActionEditModal: React.FC<QuickActionEditModalProps> = ({
  courseInstance,
  formType,
  visible,
  setShowModal,
  onConfirm,
  toggleCourseInstanceConfirmed,
  toggleCourseInstanceCanceled,
  deleteCourseInstance,
  updatingInstructor,
  updatingCourse,
  updatingStatus,
  deletingCourseInstance,
  onFilterChange,
}) => {
  const initialFormValues = { instructor: courseInstance.instructor };
  const [currentFormValues, setCurrentFormValues] = useState<
    EditableFormValues | undefined
  >(initialFormValues);

  const { t } = useTranslation();

  const displayForm = (form?: string) => {
    switch (form) {
      case FormType.SelectInstructorForm:
        return (
          <ModalForm
            formType={FormType.SelectInstructorForm}
            onChange={setCurrentFormValues}
            currentFormValues={currentFormValues}
          />
        );
      case FormType.SelectCourseForm:
        return (
          <ModalForm
            formType={FormType.SelectCourseForm}
            onChange={setCurrentFormValues}
            currentFormValues={currentFormValues}
          />
        );
      case FormType.DeleteForm:
        return <ModalForm formType={FormType.DeleteForm} />;
    }
  };

  switch (formType) {
    case 'swapInstructor':
      return (
        <SwapInstructorModal
          firstCourseInstance={courseInstance}
          setShowModal={setShowModal}
          onSubmit={onFilterChange}
        />
      );
    case 'confirm':
      return (
        <Modal
          title={getTitle(t, FormType.ConfirmForm, courseInstance.status)}
          open={visible}
          onCancel={() => {
            setShowModal?.(false);
          }}
          footer={[
            <Button key="cancel" onClick={() => setShowModal?.(false)}>
              {t('common.cancel')}
            </Button>,
            <Button
              key="confirm"
              type="primary"
              loading={updatingStatus}
              onClick={toggleCourseInstanceConfirmed}>
              {courseInstance.status !== CourseInstanceStatus.Confirmed
                ? t('common.confirm')
                : t('components.QuickActionEditModal.undoConfirm')}
            </Button>,
          ]}
          maskClosable={false}>
          {getSubtitle(t, FormType.ConfirmForm, courseInstance.status)}
        </Modal>
      );
    case 'cancel':
      return (
        <Modal
          title={getTitle(t, FormType.CancelForm, courseInstance.status)}
          open={visible}
          onCancel={() => {
            setShowModal?.(false);
          }}
          footer={[
            <Button key="cancel" onClick={() => setShowModal?.(false)}>
              {t('common.cancel')}
            </Button>,
            <Button
              key="cancelCourseInstance"
              type="primary"
              loading={updatingStatus}
              onClick={toggleCourseInstanceCanceled}>
              {courseInstance.status !== CourseInstanceStatus.Canceled
                ? t('components.QuickActionEditModal.cancel')
                : t('components.QuickActionEditModal.undoCancel')}
            </Button>,
          ]}
          maskClosable={false}>
          {getSubtitle(t, FormType.CancelForm, courseInstance.status)}
        </Modal>
      );
    case 'delete':
      return (
        <Modal
          title={t('components.QuickActionEditModal.deleteCourseInstance')}
          open={visible}
          onCancel={() => {
            setShowModal?.(false);
          }}
          footer={[
            <Button key="cancel" onClick={() => setShowModal?.(false)}>
              {t('common.cancel')}
            </Button>,
            <Button
              danger
              key="delete"
              type="primary"
              loading={deletingCourseInstance}
              onClick={deleteCourseInstance}>
              {t('common.delete')}
            </Button>,
          ]}
          maskClosable={false}>
          {t('components.QuickActionEditModal.confirmDeleteCourseInstance')}
        </Modal>
      );
    default:
      return (
        <Modal
          title={getTitle(t, formType)}
          open={visible}
          onOk={
            currentFormValues &&
            formType &&
            (() => onConfirm?.(currentFormValues, formType))
          }
          okButtonProps={{
            disabled: isEqual(initialFormValues, currentFormValues),
            loading: updatingInstructor || updatingCourse,
          }}
          onCancel={() => {
            setShowModal?.(false);
            if (setCurrentFormValues) setCurrentFormValues(undefined);
          }}
          maskClosable={false}>
          <p>{getSubtitle(t, formType)}</p>
          {displayForm(formType)}
        </Modal>
      );
  }
};

const instructorSelector = (
  t: TFunction,
  loading: boolean,
  loadingOptions: boolean,
  instructors: User[],
  currentUser?: User | null,
  hasMore?: boolean,
  onDropdownOpen?: () => void,
  onLoadMore?: () => void,
  onSearch?: (query: string) => void,
) => {
  return (
    <Select
      placeholder={t('components.QuickActionEditModal.selectInstructor')}
      showSearch
      allowClear
      loading={loading}
      filterOption={false}
      notFoundContent={loadingOptions ? <SearchLoadingIndicator /> : null}
      onDropdownVisibleChange={(open) => {
        if (open) {
          onDropdownOpen?.();
        }
      }}
      onPopupScroll={(event) => {
        const target = event.target as HTMLDivElement;
        const isNearBottom =
          target.scrollTop + target.offsetHeight >= target.scrollHeight - 32;

        if (isNearBottom && hasMore) {
          onLoadMore?.();
        }
      }}
      onSearch={onSearch}>
      {
        <>
          <Select.Option key={0} value={0}>
            <UserOption
              user={undefined}
              noUserText={t('components.QuickActionEditModal.noInstructor')}
            />
          </Select.Option>
          {instructors?.map(
            (instructor) =>
              instructor.id && (
                <Select.Option key={instructor.id} value={instructor.id}>
                  <UserOption user={instructor} currentUser={currentUser} />
                </Select.Option>
              ),
          )}
        </>
      }
    </Select>
  );
};

const courseSelector = (
  t: TFunction,
  loading: boolean,
  currentUser: User | null,
  courses?: Course[],
) => {
  return (
    <Select
      placeholder={t('components.QuickActionEditModal.selectCourse')}
      showSearch
      allowClear
      loading={loading}
      filterOption={(input, option) =>
        option?.props.children.props.course.name
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      }>
      {courses?.map((course) => (
        <Select.Option key={course.id} value={course.id}>
          <CourseOption user={currentUser} course={course} />
        </Select.Option>
      ))}
    </Select>
  );
};

const ModalForm: React.FC<{
  onChange?: (values: EditableFormValues) => void;
  formType: FormType;
  currentFormValues?: EditableFormValues;
}> = ({ onChange, formType, currentFormValues }) => {
  const [form] = Form.useForm();
  const [courses, setCourses] = useState<Course[]>();
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [currentUser] = useUser();

  const { t } = useTranslation();

  const {
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage,
    loadMoreInstructors,
    loadingInstructorOptions,
    loadingInstructors,
    searchInstructors,
  } = useInstructorOptions({
    enabled: formType === FormType.SelectInstructorForm,
    hydrateSelectedIds: [currentFormValues?.instructor?.id],
  });

  const fetchCourses = useCallback(async (instructorId?: User['id']) => {
    setLoadingCourses(true);
    try {
      const { data } = await CourseAPI.getCourses({
        userIds: instructorId ? [instructorId] : undefined,
      });
      setCourses(data);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  useEffect(() => {
    form.setFieldsValue({
      instructorId: currentFormValues?.instructor?.id ?? 0,
    });
  }, [currentFormValues, form]);

  useEffect(() => {
    switch (formType) {
      case FormType.SelectInstructorForm:
        break;
      case FormType.SelectCourseForm:
        fetchCourses(currentFormValues?.instructor?.id);
        break;
    }
  }, [currentFormValues?.instructor?.id, fetchCourses, formType]);

  const selectorOption = useCallback(() => {
    switch (formType) {
      case FormType.SelectInstructorForm:
        return instructorSelector(
          t,
          loadingInstructors,
          loadingInstructorOptions,
          instructors,
          currentUser,
          hasMoreInstructors,
          () => loadFirstInstructorPage(''),
          loadMoreInstructors,
          searchInstructors,
        );
      case FormType.SelectCourseForm:
        return courseSelector(t, loadingCourses, currentUser, courses);
    }
  }, [
    courses,
    currentUser,
    formType,
    hasMoreInstructors,
    instructors,
    loadFirstInstructorPage,
    loadMoreInstructors,
    loadingCourses,
    loadingInstructorOptions,
    loadingInstructors,
    searchInstructors,
    t,
  ]);

  return (
    <>
      <Form
        form={form}
        onValuesChange={(values) => {
          onChange?.({
            instructor:
              values.instructorId !== undefined
                ? values.instructorId === 0
                  ? null
                  : instructors?.find(
                      (instructor) => instructor.id === values.instructorId,
                    )
                : currentFormValues?.instructor,
            course: values.courseId
              ? courses?.find((course) => course.id === values.courseId)
              : currentFormValues?.course,
          });
        }}>
        <Form.Item name={`${formType}`}>{selectorOption()}</Form.Item>
      </Form>
    </>
  );
};

export default QuickActionEditModal;
